import type { DayNightPhase } from "../../models/dayNightPhase.ts";
import { DAY_NIGHT_PHASES } from "../../models/dayNightPhase.ts";
import type { Season } from "../../models/season.ts";
import type { TileTerrain, Weather } from "../../models/worldState.ts";
import { WEATHER_CONDITIONS } from "../../models/worldState.ts";

export type { Weather };
export { WEATHER_CONDITIONS };

export interface TemperatureBand {
  min: number;
  max: number;
}

export const TERRAIN_TEMPERATURE_DELTA: Record<TileTerrain, number> = {
  mountain: -8,
  desert: 10,
  swamp: -2,
  forest: -3,
  plains: 0,
  river: -1,
};

const springDay: TemperatureBand = { min: 8, max: 18 };
const springDusk: TemperatureBand = { min: 5, max: 14 };
const springNight: TemperatureBand = { min: 0, max: 8 };
const springDawn: TemperatureBand = { min: 3, max: 12 };

const summerDay: TemperatureBand = { min: 22, max: 34 };
const summerDusk: TemperatureBand = { min: 18, max: 28 };
const summerNight: TemperatureBand = { min: 14, max: 22 };
const summerDawn: TemperatureBand = { min: 16, max: 24 };

const autumnDay: TemperatureBand = { min: 10, max: 20 };
const autumnDusk: TemperatureBand = { min: 6, max: 16 };
const autumnNight: TemperatureBand = { min: 2, max: 10 };
const autumnDawn: TemperatureBand = { min: 4, max: 14 };

const winterDay: TemperatureBand = { min: -6, max: 4 };
const winterDusk: TemperatureBand = { min: -10, max: 0 };
const winterNight: TemperatureBand = { min: -18, max: -6 };
const winterDawn: TemperatureBand = { min: -12, max: -2 };

export const SEASON_PHASE_TEMP_BANDS: Record<
  Season,
  Record<DayNightPhase, TemperatureBand>
> = {
  spring: {
    day: springDay,
    dusk: springDusk,
    night: springNight,
    dawn: springDawn,
  },
  summer: {
    day: summerDay,
    dusk: summerDusk,
    night: summerNight,
    dawn: summerDawn,
  },
  autumn: {
    day: autumnDay,
    dusk: autumnDusk,
    night: autumnNight,
    dawn: autumnDawn,
  },
  winter: {
    day: winterDay,
    dusk: winterDusk,
    night: winterNight,
    dawn: winterDawn,
  },
};

type WeatherWeightTable = Record<Weather, number>;

function weights(
  clear: number,
  cloudy: number,
  overcast: number,
  rainy: number,
  snowy: number,
): WeatherWeightTable {
  return { clear, cloudy, overcast, rainy, snowy };
}

const BASE_WEATHER_WEIGHTS: Record<
  Season,
  Record<DayNightPhase, WeatherWeightTable>
> = {
  spring: {
    day: weights(3, 2, 1, 2, 0),
    dusk: weights(2, 2, 2, 2, 0),
    night: weights(1, 2, 2, 2, 0),
    dawn: weights(2, 2, 2, 1, 0),
  },
  summer: {
    day: weights(5, 2, 1, 0, 0),
    dusk: weights(3, 2, 1, 1, 0),
    night: weights(2, 2, 1, 1, 0),
    dawn: weights(3, 2, 1, 1, 0),
  },
  autumn: {
    day: weights(2, 3, 2, 2, 0),
    dusk: weights(1, 2, 3, 2, 0),
    night: weights(1, 2, 2, 3, 0),
    dawn: weights(1, 2, 3, 2, 0),
  },
  winter: {
    day: weights(1, 2, 2, 1, 2),
    dusk: weights(1, 2, 2, 1, 3),
    night: weights(0, 1, 2, 1, 4),
    dawn: weights(1, 2, 2, 1, 3),
  },
};

const TERRAIN_WEATHER_MODIFIERS: Record<TileTerrain, Partial<WeatherWeightTable>> = {
  mountain: { cloudy: 1, overcast: 1, snowy: 3 },
  desert: { clear: 3, cloudy: -1, rainy: -2 },
  swamp: { overcast: 2, rainy: 3 },
  forest: { cloudy: 1, rainy: 1 },
  plains: {},
  river: { cloudy: 1, rainy: 2 },
};

export function getWeatherWeights(
  season: Season,
  phase: DayNightPhase,
  terrain: TileTerrain,
): WeatherWeightTable {
  const base = BASE_WEATHER_WEIGHTS[season][phase];
  const modifiers = TERRAIN_WEATHER_MODIFIERS[terrain];
  const combined = {} as WeatherWeightTable;
  for (const condition of WEATHER_CONDITIONS) {
    combined[condition] = Math.max(
      0,
      base[condition] + (modifiers[condition] ?? 0),
    );
  }
  return combined;
}

export function assertWorldConditionsConfig(): void {
  const seasons: Season[] = ["spring", "summer", "autumn", "winter"];
  for (const season of seasons) {
    for (const phase of DAY_NIGHT_PHASES) {
      const band = SEASON_PHASE_TEMP_BANDS[season][phase];
      if (band.min > band.max) {
        throw new Error(`Invalid temperature band for ${season} ${phase}`);
      }
    }
  }
}

assertWorldConditionsConfig();
